import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Notify, toast } from "@/components/ui/notify";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useOrgAttention } from "@/features/attention/hooks";
import { occurrenceTitle } from "@/features/attention/occurrence-meta";
import { useResolveAlarm } from "@/features/alarms/hooks";
import { JustifyEventDialog } from "@/features/machine-sidebar/JustifyEventDialog";
import { useAuthStore } from "@/stores/auth.store";
import { useRealtimeRooms } from "@/lib/use-realtime-rooms";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import type { AttentionOccurrence } from "@/types/attention.types";
import { AckDialog } from "./AckDialog";
import { MarkFalseDialog } from "./MarkFalseDialog";
import { AlarmsTable } from "./AlarmsTable";
import {
  AlarmsFilters,
  type TypeFilter,
  type SeverityFilter,
  type FlappingFilter,
} from "./AlarmsFilters";

type Scope = "pending" | "resolved";

const PAGE_SIZE = 50; // Tamanho de página da lista consolidada.

const TABS: { value: Scope; label: string }[] = [
  { value: "pending", label: "Pendentes" },
  { value: "resolved", label: "Histórico" },
];

export function AlarmsPage() {
  const me = useAuthStore((s) => s.user)!;
  const canResolve = me.role === "GESTOR";
  const resolve = useResolveAlarm();

  const [searchParams] = useSearchParams();
  const [scope, setScope] = useState<Scope>("pending");
  const [lineId, setLineId] = useState<string>(searchParams.get("lineId") ?? "");
  const [type, setType] = useState<TypeFilter>("");
  const [severity, setSeverity] = useState<SeverityFilter>("");
  const [flapping, setFlapping] = useState<FlappingFilter>("");
  const [ackTarget, setAckTarget] = useState<AttentionOccurrence | null>(null);
  const [falseTarget, setFalseTarget] = useState<AttentionOccurrence | null>(null);
  const [justifyId, setJustifyId] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  // Qualquer mudança de filtro/aba volta à 1ª página. Ajuste de estado
  // em render (padrão React p/ resetar state quando um valor derivado muda), não em effect.
  const filterKey = `${scope}|${lineId}|${type}|${severity}|${flapping}`;
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey);
  if (filterKey !== prevFilterKey) {
    setPrevFilterKey(filterKey);
    setOffset(0);
  }

  const attentionQuery = useOrgAttention({
    scope,
    lineId: lineId || undefined,
    type: type || undefined,
    severity: severity || undefined,
    flapping: flapping === "true" ? true : undefined,
    limit: PAGE_SIZE,
    offset,
  });
  const { data, isLoading } = attentionQuery;

  async function onResolve(occ: AttentionOccurrence) {
    if (!occ.alarm) return;
    const ok = await Notify.confirm({
      variant: "info",
      title: `Resolver alarme "${occurrenceTitle(occ)}"?`,
      confirmLabel: "Resolver",
    });
    if (!ok) return;
    try {
      await resolve.mutateAsync(occ.alarm.refId);
      toast.success("Alarme resolvido.");
    } catch (err) {
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao resolver.", { description: msg });
    }
  }

  const items = useMemo(() => data?.items ?? [], [data]);
  // Entra nos rooms das linhas dos alarmes visíveis (room-per-line) p/ eventos ao vivo.
  useRealtimeRooms(useMemo(() => items.map((o) => o.lineId), [items]));

  return (
    <section>
      <PageHeader
        title="Alarmes"
        subtitle="Alarmes e eventos de atenção de todas as linhas."
        actions={
          <AlarmsFilters
            lineId={lineId}
            type={type}
            severity={severity}
            flapping={flapping}
            onLineChange={setLineId}
            onTypeChange={setType}
            onSeverityChange={setSeverity}
            onFlappingChange={setFlapping}
          />
        }
      />

      <div
        role="tablist"
        aria-label="Filtrar por estado"
        className="mb-4 inline-flex rounded-md border border-border-subtle p-0.5"
      >
        {TABS.map((t) => (
          <Button
            key={t.value}
            role="tab"
            aria-selected={scope === t.value}
            variant="ghost"
            size="sm"
            className={cn(
              "h-8 rounded",
              scope === t.value ? "bg-bg-muted-rd text-text-primary" : "text-text-secondary"
            )}
            onClick={() => setScope(t.value)}
          >
            {t.label}
          </Button>
        ))}
      </div>

      {data ? (
        <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {items.length === 0
              ? "Nenhum registro"
              : `Mostrando ${data.offset + 1}–${data.offset + items.length} de ${data.total}`}
          </span>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={data.offset === 0}
              onClick={() => setOffset(Math.max(0, data.offset - PAGE_SIZE))}
            >
              ‹ Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={data.offset + items.length >= data.total}
              onClick={() => setOffset(data.offset + PAGE_SIZE)}
            >
              Próxima ›
            </Button>
          </div>
        </div>
      ) : null}

      {attentionQuery.isError ? (
        // Severidade alta: NUNCA deixar a AlarmsTable renderizar aqui em caso de erro
        // (o `emptyState` dela para "pending" é "Nenhuma pendência de atenção no momento. 🎉",
        // um falso positivo perigoso numa tela de alarmes).
        <Notify.QueryError
          error={attentionQuery.error}
          onRetry={() => void attentionQuery.refetch()}
        />
      ) : isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : (
        <AlarmsTable
          occurrences={items}
          scope={scope}
          canResolve={canResolve}
          onAck={setAckTarget}
          onResolve={(o) => void onResolve(o)}
          onJustify={(o) => o.justification && setJustifyId(o.justification.refId)}
          onMarkFalse={setFalseTarget}
        />
      )}

      <AckDialog occurrence={ackTarget} open={!!ackTarget} onClose={() => setAckTarget(null)} />
      <MarkFalseDialog
        occurrence={falseTarget}
        open={!!falseTarget}
        onClose={() => setFalseTarget(null)}
      />
      {justifyId ? (
        <JustifyEventDialog eventId={justifyId} open onClose={() => setJustifyId(null)} />
      ) : null}
    </section>
  );
}
